import axios from "axios";
//@ts-ignore
import { saveAs } from "file-saver";
import { whatIs } from "lib";
import Datasource from "lib/datasource";
import _ from "lodash";
import { postEvent } from "./history";
import { memoAC } from "./memoize";
import moment from "moment";
import { getAuthTokenFromCookie } from 'lib/hooks';

/** Dispatches a Document request and makes an API call
 * @param {Document} document
 * @yields `GET_DOCUMENTS_REQUEST`
 * @yields recieveDocument
 * @yields getsCommentsFor
 */
// F8: Default viewEvent changed to false. Callers must NOT pass viewEvent=true.
// The canonical View-tracking path is recordDocumentView() below.
// DO NOT use postEvent("View") for Document or Form resources from Show pages.
export const getDocument = memoAC((id: string, viewEvent: boolean = false) => (dispatch: any) => {
	dispatch({ type: "GET_DOCUMENT_REQUEST" });
	axios.get(`${process.env.REACT_APP_API_ENDPOINT_V2}documents/${id}`, {
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${getAuthTokenFromCookie()}`,
		}
	})
	.then((res) => {
		let document = Datasource.getNormalizedData(res.data, "document") as tcpinpoint.Document;
		// dispatch(getCommentsFor(document));
		if (viewEvent) {
			dispatch(postEvent("View", document));
		}
		dispatch({ type: "GET_DOCUMENT_SUCCESS", response: { status: 200 }, resource: document });
	})
	.catch((e) => { dispatch({ type: "GET_DOCUMENT_FAILED", resource: document, response: e }); });
});

/**
 * CANONICAL VIEW-TRACKING CALL for Documents.
 * Records a server-side "View" Activity for the given document.
 * Server applies a 5-minute dedupe per (user, document); this thunk
 * fires once per document open. Failures are swallowed (console only)
 * so the user never sees an error from a tracking call.
 *
 * F8: This is the ONLY correct way to emit a View event for a Document.
 * DO NOT use postEvent("View", doc) anywhere in Document or Form Show pages.
 * postEvent bypasses server-side dedupe and will double-log.
 * Contract: api-contract.md §2.6, §2.10.
 */
export const recordDocumentView = (documentId: string) => (dispatch: any) => {
	dispatch({ type: "RECORD_DOCUMENT_VIEW_REQUEST", documentId });
	return axios.post(
		`${process.env.REACT_APP_API_ENDPOINT_V2}documents/${documentId}/record-view`,
		{},
		{
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${getAuthTokenFromCookie()}`,
			},
		}
	)
	.then((res) => {
		dispatch({ type: "RECORD_DOCUMENT_VIEW_SUCCESS", documentId, status: res.status });
	})
	.catch((e) => {
		// Tracking failures must not block UX. Do not toast, do not retry.
		// eslint-disable-next-line no-console
		console.warn(`recordDocumentView failed for ${documentId}:`, e?.response?.status, e?.message);
		dispatch({ type: "RECORD_DOCUMENT_VIEW_FAILED", documentId, response: e });
	});
};

/** Deletes a Document */
export const deleteDocument = memoAC((document: tcpinpoint.Document) => (dispatch: any) => {
	dispatch({ type: "DELETE_DOCUMENT_REQUEST" });
	axios.delete(`${process.env.REACT_APP_API_ENDPOINT_V2}documents/${document.id}`, {
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${getAuthTokenFromCookie()}`,
		}
	})
	.then((res) => {
		dispatch({ type: "DELETE_DOCUMENT_SUCCESS", response: { status: 200 }, resource: document });
		dispatch(getDocument(document.id, false));
	})
	.catch((e) => { dispatch({ type: "DELETE_DOCUMENT_FAILED", resource: document, response: e }); });
});

/** Updates a Document
 * @param {Document} document
 */
export const putDocument = memoAC((document: tcpinpoint.Document) => (dispatch: any) => {
	dispatch({ type: "PUT_DOCUMENTS_REQUEST" });
	axios.put(`${process.env.REACT_APP_API_ENDPOINT_V2}documents/${document.id}`, { document }, {
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${getAuthTokenFromCookie()}`,
		}
	})
	.then((res) => {
		let document = Datasource.getNormalizedData(res.data, "document") as tcpinpoint.Document;
		dispatch(getDocument(document.id, false));
		dispatch({ type: "PUT_DOCUMENTS_SUCCESS" });
	})
	.catch((e) => { dispatch({ type: "PUT_DOCUMENTS_FAILED", resource: document, response: e }); });
});

/** Upload a new Revision to the Document
 * @param {document} document
 * @param {string} uri
 */
export const postDocumentRevision = memoAC(
	(document: tcpinpoint.Document, uri: string, callback: any) => (dispatch: any) => {
		dispatch({ type: "POST_DOCUMENT_REVISION_REQUEST" });
		axios.put(`${process.env.REACT_APP_API_ENDPOINT_V2}documents/${document.id}/revision`, { uri, source: "web" }, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${getAuthTokenFromCookie()}`,
			}
		})
		.then((res) => {
			let document = Datasource.getNormalizedData(res.data, "document") as tcpinpoint.Document;
			dispatch({ type: "POST_DOCUMENT_REVISION_SUCCESS", response: { status: 200 }, resource: document });
			dispatch(createThumbnail(document));
			dispatch(getExif(document));
			setTimeout(() => {
				dispatch({ type: "FINALISE_DOCUMENT", document });
				if (typeof callback === "function") callback(moment());
			}, 5000);
		})
		.catch((e) => { dispatch({ type: "POST_DOCUMENT_REVISION_FAILED", resource: document, response: e }); });
	}
);

export const exportToDocument = memoAC((document: tcpinpoint.Document) => (dispatch: any) => {
	dispatch({ type: "EXPORT_DOCUMENT_REQUEST" });
	axios.post(`${process.env.REACT_APP_API_ENDPOINT_V2}documents`, { document }, {
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${getAuthTokenFromCookie()}`,
		}
	})
	.then((res) => {
		dispatch({ type: "EXPORT_DOCUMENT_SUCCESS" });
		dispatch(getDocument(document.id));
	})
	.catch((e) => { dispatch({ type: "EXPORT_DOCUMENT_FAILED", resource: document, response: e }); });
});

/**
 * UPLOAD-FIRST document creation (Sim, 24 Sep 2026).
 *
 *   1. POST <parent>/documents/upload_url   presigned POST, no Document yet
 *   2. POST the file straight to S3
 *   3. POST <parent>/documents  { uri }     the Document is created WITH its
 *                                           revision, only now the file is there
 *
 * The old order created the Document first, so a failed, cancelled or
 * abandoned upload (tab closed, network dropped) left an empty record behind —
 * and the create had already sent the "uploaded a document" email/activity and
 * advanced the submission round. Here nothing is recorded until S3 has the file,
 * and the API checks the object really exists before creating the row.
 *
 * `onUploadProgress` / `cancelToken` go to the S3 request, the only slow step.
 * Resolves with the normalized Document; rejects on any failure (an axios
 * cancel rejects with axios.isCancel(e) === true).
 */
export const createDocumentUploadFirst = async (
	parentPath: string,
	file: File,
	attributes: Record<string, any> = {},
	options: { cancelToken?: any; onUploadProgress?: (event: any) => void } = {}
): Promise<tcpinpoint.Document> => {
	const headers = {
		"Content-Type": "application/json",
		Authorization: `Bearer ${getAuthTokenFromCookie()}`,
	};
	const document = {
		mimetype: file.type,
		name: file.name,
		filesize_bytes: file.size,
		...attributes,
	};

	const urlRes = await axios.post(
		`${process.env.REACT_APP_API_ENDPOINT_V2}${parentPath}/documents/upload_url`,
		{ document },
		{ headers }
	);
	const presigned = _.get(urlRes, "data.presigned_post_url");
	if (!presigned || !presigned.url || !presigned.fields) throw new Error("No presigned upload URL returned");

	const data = new FormData();
	_.forEach(presigned.fields, (value: any, key: string) => data.append(key, value));
	data.append("file", file);
	await axios.post(presigned.url, data, {
		cancelToken: options.cancelToken,
		onUploadProgress: options.onUploadProgress,
	});

	const createRes = await axios.post(
		`${process.env.REACT_APP_API_ENDPOINT_V2}${parentPath}/documents`,
		{ document, uri: presigned.fields.key, source: "web" },
		{ headers }
	);
	return Datasource.getNormalizedData(createRes.data, "document") as tcpinpoint.Document;
};

/**
 * Runs createDocumentUploadFirst with the upload tray (components/Documents/
 * Uploads) and the thumbnail / EXIF follow-ups wired in, and dispatches the same
 * action vocabulary the old create-then-upload chain did, so ReviewActionPanel's
 * interceptor still sees FINALISE_DOCUMENT on success and a *_FAILED on failure.
 *
 * The tray is keyed on a document id, and there is no Document while the file
 * is uploading, so it tracks a placeholder until the real one exists.
 */
export const uploadDocumentWithTray = (
	parentPath: string,
	file: File,
	attributes: Record<string, any>,
	dispatch: any,
	onCreated?: (document: tcpinpoint.Document) => void,
	callback?: any
): Promise<tcpinpoint.Document | undefined> => {
	const placeholder = { id: `upload-${+new Date()}-${Math.random().toString(36).slice(2)}`, name: file.name } as any;
	const source = axios.CancelToken.source();
	const now = +new Date();

	dispatch({ type: "POST_DOCUMENT_REQUEST" });
	dispatch({ type: "UPLOAD_DOCUMENT_REQUEST", document: placeholder, cancelToken: source });

	return createDocumentUploadFirst(parentPath, file, attributes, {
		cancelToken: source.token,
		onUploadProgress: (event) => dispatch(progressDocumentUpload(event, placeholder, now, source)),
	})
		.then(
			(document) => {
				// The Document EXISTS from here on. Nothing below may send it to the
				// failure branch: a caller's callback throwing (Documents/CardDynamic
				// called an unpassed `momentUpdate` on the property Documents tab)
				// used to land in the catch below and paint "Something Went Wrong"
				// beside the "Done!" of a perfectly good upload. Each follow-up is
				// isolated and only logged.
				const safely = (step: string, fn: () => void) => {
					try {
						fn();
					} catch (e) {
						// eslint-disable-next-line no-console
						console.error(`Document upload follow-up "${step}" failed for ${document.id}`, e);
					}
				};
				dispatch({ type: "UPLOAD_DOCUMENT_CLEAR", document: placeholder });
				safely("POST_DOCUMENT_SUCCESS", () => dispatch({ type: "POST_DOCUMENT_SUCCESS" }));
				safely("UPLOAD_DOCUMENT_SUCCESS", () =>
					dispatch({ type: "UPLOAD_DOCUMENT_SUCCESS", response: { status: 200 }, resource: document, document })
				);
				safely("getDocument", () => dispatch(getDocument(document.id, false)));
				if (onCreated) safely("onCreated", () => onCreated(document));
				if (typeof callback === "function") safely("callback", () => callback(moment()));
				safely("thumbnail", () => dispatch(createThumbnail(document)));
				safely("exif", () => dispatch(getExif(document)));
				setTimeout(() => {
					dispatch({ type: "FINALISE_DOCUMENT", document });
					if (typeof callback === "function") safely("callback", () => callback(moment()));
				}, 5000);
				return document;
			}
		)
		.catch((response) => {
			if (axios.isCancel(response)) {
				// Nothing was created, so there is nothing to undo or delete.
				dispatch({ type: "UPLOAD_DOCUMENT_CANCELLED", document: placeholder, response });
			} else {
				dispatch({ type: "UPLOAD_DOCUMENT_FAILED", document: placeholder, response });
				dispatch({ type: "POST_DOCUMENT_FAILED", response });
			}
			return undefined;
		});
};

/**
 * Puts one file on the upload tray (components/Documents/Uploads) for an upload
 * that is not a Document — the Forms tab's .docx uploads. Hand `cancelToken` and
 * `onUploadProgress` to the S3 request, then call exactly one of `done`,
 * `failed` or `cancelled`. `failed` leaves the entry up with "Something Went
 * Wrong" until closed; the others clear it.
 */
export const startUploadTray = (dispatch: any, name: string) => {
	const placeholder = { id: `upload-${+new Date()}-${Math.random().toString(36).slice(2)}`, name } as any;
	const source = axios.CancelToken.source();
	const now = +new Date();
	dispatch({ type: "UPLOAD_DOCUMENT_REQUEST", document: placeholder, cancelToken: source });
	return {
		cancelToken: source.token,
		onUploadProgress: (event: any) => dispatch(progressDocumentUpload(event, placeholder, now, source)),
		done: () => dispatch({ type: "UPLOAD_DOCUMENT_CLEAR", document: placeholder }),
		failed: (response?: any) => dispatch({ type: "UPLOAD_DOCUMENT_FAILED", document: placeholder, response }),
		cancelled: (response?: any) => dispatch({ type: "UPLOAD_DOCUMENT_CANCELLED", document: placeholder, response }),
	};
};

/** Creates a Document — upload first, see createDocumentUploadFirst. */
export const postDocument = memoAC(
	(resource: tcpinpoint.Resource, file: File, comment: string = "", callback: any, tags?: string[]) =>
		(dispatch: any) => {
			const { link, id } = resource;
			const attributes = tags && tags.length ? { tags } : {};
			return uploadDocumentWithTray(`${whatIs(link)}/${id}`, file, attributes, dispatch, (document) => {
				if (comment === "") return;
				axios.post(`${process.env.REACT_APP_API_ENDPOINT_V2}comments`, {
					global_id: link,
					body: comment,
					metadata: {
						ogSiteName: "tcpinpoint",
						ogTitle: document.name,
						ogUrl: `${process.env.REACT_APP_HOST}documents/${document.id}`,
						ogDescription: "",
					}
				},
				{
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${getAuthTokenFromCookie()}`,
					}
				})
				.then((res) => {})
				.catch((e) => {});
			}, callback);
		}
);

/** Creates a Thumbnail */
const createThumbnail = memoAC((document: tcpinpoint.Document) => (dispatch: any) => {
	dispatch({ type: "POST_THUMBNAIL_REQUEST", document });
	axios.put(`${process.env.REACT_APP_API_ENDPOINT_V2}documents/${document.id}/thumbnail`, {}, {
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${getAuthTokenFromCookie()}`,
		}
	})
	.then((res) => {
		const metadata = _.assign({}, document.metadata, res.data);
		const d = _.assign({}, document, { metadata });
		dispatch({ type: "POST_THUMBNAIL_SUCCESS", response: { status: 200 }, resource: res.data, document: d });
		dispatch(putDocument(d));
		dispatch(getExif(d));
	})
	.catch((e) => { dispatch({ type: "POST_THUMBNAIL_FAILED", response: e }); });
});

/** Retrieves the EXIF's metadata */
const getExif = memoAC((document: tcpinpoint.Document) => (dispatch: any) => {
	dispatch({ type: "GET_EXIF_REQUEST" });
	axios.get(`${process.env.REACT_APP_API_ENDPOINT_V2}documents/${document.id}/exif`, {
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${getAuthTokenFromCookie()}`,
		}
	})
	.then((res) => {
		dispatch({ type: "GET_EXIF_SUCCESS", response: { status: 200 }, resource: res.data });
		const metadata = _.assign({}, document.metadata, res.data);
		dispatch(putDocument(_.assign({}, document, { metadata })));
	})
	.catch((e) => { dispatch({ type: "GET_EXIF_FAILED", resource: document, response: e }); });
});

/** Updates the progress of Document upload
 * @param {ProgressEvent} progressEvent https://developer.mozilla.org/en-US/docs/Web/API/ProgressEvent
 * @param {Document} document
 * @param {number} timestamp
 * @param {cancelToken} cancelToken
 */
const progressDocumentUpload = memoAC(
	(progressEvent: ProgressEvent, document: tcpinpoint.Document, timestamp: number, cancelToken: any) =>
		(dispatch: any) => {
			if (progressEvent.lengthComputable) {
				/** Percent of Upload Completed */
				const percentComplete = Math.round((progressEvent.loaded / progressEvent.total) * 100);
				/** Number of seconds since upload began */
				const deltaSeconds = (+new Date() - timestamp) / 1000;
				/** Estimated time to completing upload */
				const estimatedSeconds = Math.abs(Math.round(deltaSeconds / (percentComplete / 100)));
				/** Estimated time remaining */
				const remainingSeconds = Math.abs(Math.round(estimatedSeconds - deltaSeconds));
				dispatch({
					type: "UPLOAD_DOCUMENT_PROGRESS",
					state: "PROGRESS",
					document: document,
					finished: percentComplete === 100 || !percentComplete,
					total: progressEvent.total,
					loaded: progressEvent.loaded,
					percentComplete: percentComplete,
					estimatedSeconds: estimatedSeconds,
					remainingSeconds: remainingSeconds,
					cancelToken,
				} as tcpinpoint.DocumentUpload);
			}
		}
);

export const downloadDocument = memoAC((document: tcpinpoint.Document) => async (dispatch: any) => {
	dispatch(postEvent("Read", document));
	axios.get(`${process.env.REACT_APP_API_ENDPOINT_V2}documents/${document.id}`, {
		params: {
			fields: JSON.stringify({ document: ["name"] })
		},
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${getAuthTokenFromCookie()}`,
		}
	})
	.then((res) => {
		saveAs(document.revisions[0].signed_uri, _.get(res, "data.attributes.name", ""));
	})
	.catch((e) => {});
});

export const upload = memoAC((document: tcpinpoint.Document, file: File, callback: any) => (dispatch: any) => {
	const now = +new Date();
	const CancelToken = axios.CancelToken;
	const source = CancelToken.source();
	dispatch({ type: "UPLOAD_DOCUMENT_REQUEST", cancelToken: source });
	if (document.presigned_post_url) {
		const data = new FormData();
		data.append("key", document.presigned_post_url.fields["key"]);
		data.append("Expires", document.presigned_post_url.fields["Expires"]);
		data.append("success_action_status", document.presigned_post_url.fields["success_action_status"]);
		data.append("policy", document.presigned_post_url.fields["policy"]);
		data.append("x-amz-credential", document.presigned_post_url.fields["x-amz-credential"]);
		data.append("x-amz-algorithm", document.presigned_post_url.fields["x-amz-algorithm"]);
		data.append("x-amz-date", document.presigned_post_url.fields["x-amz-date"]);
		data.append("x-amz-signature", document.presigned_post_url.fields["x-amz-signature"]);
		data.append("acl", document.presigned_post_url.fields["acl"]);
		data.append("file", file);
		axios
			.post(document.presigned_post_url.url, data, {
				cancelToken: source.token,
				onUploadProgress: (event) => dispatch(progressDocumentUpload(event, document, now, source)),
			})
			.then((response) => _.get(response, "data", ""))
			//@ts-ignore
			.then((body) => new window.DOMParser().parseFromString(body, "text/xml"))
			.then((xml) => xml.getElementsByTagName("Key")[0].innerHTML)
			.then((uri) => {
				dispatch({ type: "UPLOAD_DOCUMENT_SUCCESS", response: { status: 200 }, resource: document, uri });

				dispatch(postDocumentRevision(document, uri, callback));
			})
			.catch((response) => {
				if (axios.isCancel(response)) {
					dispatch({ type: "UPLOAD_DOCUMENT_CANCELLED", document, response });
					dispatch(postEvent("Undo", document));
					dispatch(deleteDocument(document));
				} else {
					dispatch({ type: "UPLOAD_DOCUMENT_FAILED", document, response });
				}
			});
	} else {
		dispatch({ type: "UPLOAD_DOCUMENT_FAILED", document });
	}
});
